<!--
  The quality area home: every job site, active first, finished collapsed
  below. Open a site to see its forms; "New site" starts one.

  Look: a navy hero band carrying the headline numbers (sites, forms, last
  activity), then site cards on the stone ground with a safety-orange rail.
  Everything is high-contrast and flat — no blur, no glass — because this is
  read in daylight on a scratched phone. Motion is 200 ms and disabled under
  prefers-reduced-motion.
-->
<script lang="ts">
  import { api } from '../../lib/api';
  import { linkHandler, navigate, routePath } from '../../lib/router';
  import { notifyError } from '../../lib/store';
  import type { SiteSummary } from '../../lib/types';

  let sites = $state<SiteSummary[]>([]);
  let loading = $state(true);
  let search = $state('');
  let showFinished = $state(false);

  $effect(() => {
    api
      .listSites()
      .then((r) => { sites = r.sites; })
      .catch((cause) => notifyError(cause, 'Could not load the sites'))
      .finally(() => { loading = false; });
  });

  function matches(s: SiteSummary): boolean {
    const q = search.trim().toLowerCase();
    return q === '' || s.name.toLowerCase().includes(q) || s.city.toLowerCase().includes(q);
  }
  const active = $derived(sites.filter((s) => s.status === 'active' && matches(s)));
  const finished = $derived(sites.filter((s) => s.status === 'finished' && matches(s)));

  function formCount(s: SiteSummary): number {
    return s.jha_count + s.field_test_count + s.equipment_count + s.daily_report_count + s.contract_report_count;
  }
  const totalForms = $derived(sites.reduce((n, s) => n + formCount(s), 0));
  const lastActivity = $derived(
    sites.reduce<string | null>((latest, s) => (s.last_activity && (!latest || s.last_activity > latest) ? s.last_activity : latest), null)
  );

  function place(s: SiteSummary): string {
    const bits = [s.street, s.city].filter((b) => b.trim() !== '');
    return bits.length ? bits.join(', ') : 'No address yet';
  }
  function day(ts: string | null): string {
    return ts ? ts.slice(0, 10) : '—';
  }
</script>

<section class="hero">
  <div class="hero__inner">
    <div>
      <p class="hero__kicker">Quality forms</p>
      <h1 class="hero__title">Job sites</h1>
    </div>
    <button class="hero__cta" type="button" onclick={() => navigate(routePath.newQualitySite())}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
      New site
    </button>
  </div>
  <div class="hero__stats" aria-label="Summary">
    <div><strong>{sites.filter((s) => s.status === 'active').length}</strong><span>active sites</span></div>
    <div><strong>{totalForms}</strong><span>forms filed</span></div>
    <div><strong>{day(lastActivity)}</strong><span>last activity</span></div>
  </div>
</section>

<label class="find">
  <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
  <input type="search" bind:value={search} placeholder="Find a site or city" aria-label="Find a site or city" />
</label>

{#if loading}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else if sites.length === 0}
  <div class="empty">
    <strong>No sites yet</strong>
    <span class="meta">Start with the one you are on — every form you file will hang off it.</span>
    <button class="btn btn--primary" type="button" onclick={() => navigate(routePath.newQualitySite())}>Create the first site</button>
  </div>
{:else}
  <ul class="sites">
    {#each active as s, i (s.id)}
      <li style="--i: {i}">
        <a class="site" href={routePath.qualitySite(s.id)} onclick={linkHandler}>
          <span class="site__rail" aria-hidden="true"></span>
          <span class="site__main">
            <span class="site__name">{s.name}</span>
            <span class="site__place">{place(s)}</span>
          </span>
          <span class="site__counts">
            <span class="site__count"><strong>{s.daily_report_count}</strong><small>daily</small></span>
            <span class="site__count"><strong>{s.contract_report_count}</strong><small>contract</small></span>
            <span class="site__count"><strong>{s.jha_count}</strong><small>JHA</small></span>
            <span class="site__count"><strong>{s.equipment_count}</strong><small>equip</small></span>
            <span class="site__count"><strong>{s.field_test_count}</strong><small>tests</small></span>
          </span>
          <span class="site__when">
            {#if s.last_activity}<small>last activity</small><strong>{day(s.last_activity)}</strong>{:else}<small>nothing filed yet</small>{/if}
          </span>
          <svg class="site__chev" viewBox="0 0 24 24" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
        </a>
      </li>
    {/each}
  </ul>
  {#if active.length === 0}<p class="meta center">No active sites match.</p>{/if}

  {#if finished.length > 0}
    <button class="finished-toggle" type="button" aria-expanded={showFinished} onclick={() => (showFinished = !showFinished)}>
      <svg viewBox="0 0 24 24" aria-hidden="true" class:open={showFinished}><path d="m6 9 6 6 6-6" /></svg>
      Finished sites ({finished.length})
    </button>
    {#if showFinished}
      <ul class="sites sites--finished">
        {#each finished as s, i (s.id)}
          <li style="--i: {i}">
            <a class="site" href={routePath.qualitySite(s.id)} onclick={linkHandler}>
              <span class="site__rail site__rail--done" aria-hidden="true"></span>
              <span class="site__main">
                <span class="site__name">{s.name}</span>
                <span class="site__place">{place(s)}{s.end_date ? ` · finished ${s.end_date}` : ''}</span>
              </span>
              <span class="site__when"><small>forms</small><strong>{formCount(s)}</strong></span>
              <svg class="site__chev" viewBox="0 0 24 24" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
            </a>
          </li>
        {/each}
      </ul>
    {/if}
  {/if}
{/if}

<style>
  /* ---- hero ------------------------------------------------------------ */
  .hero {
    background: linear-gradient(160deg, var(--brand-dark) 0%, var(--brand) 70%);
    color: #fff;
    border-radius: var(--radius-lg);
    padding: 1.1rem 1.1rem 0.9rem;
    margin-bottom: 0.9rem;
    box-shadow: var(--shadow);
  }
  .hero__inner {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.8rem;
  }
  .hero__kicker {
    margin: 0 0 0.15rem;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--accent);
  }
  .hero__title {
    margin: 0;
    font-size: 1.8rem;
    letter-spacing: -0.02em;
    line-height: 1.05;
  }
  .hero__cta {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    min-height: var(--tap);
    padding: 0 1rem;
    border: 0;
    border-radius: 999px;
    background: var(--accent);
    color: #1a1207;
    font: inherit;
    font-weight: 700;
    cursor: pointer;
    touch-action: manipulation;
    transition: transform 150ms ease-out, filter 150ms ease-out;
  }
  .hero__cta:hover { filter: brightness(1.06); }
  .hero__cta:active { transform: scale(0.97); }
  .hero__cta svg { width: 1.1rem; height: 1.1rem; fill: none; stroke: currentColor; stroke-width: 2.5; stroke-linecap: round; }

  .hero__stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.6rem;
    margin-top: 1rem;
    padding-top: 0.8rem;
    border-top: 1px solid rgb(255 255 255 / 0.18);
  }
  .hero__stats div { display: grid; gap: 0.1rem; }
  .hero__stats strong { font-size: 1.35rem; font-variant-numeric: tabular-nums; letter-spacing: -0.01em; }
  .hero__stats span { font-size: 0.74rem; opacity: 0.8; text-transform: uppercase; letter-spacing: 0.06em; }

  /* ---- find ------------------------------------------------------------ */
  .find {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: var(--paper);
    border: 1.5px solid var(--line);
    border-radius: 999px;
    padding: 0 0.9rem;
    margin-bottom: 0.9rem;
    min-height: var(--tap);
  }
  .find:focus-within { border-color: var(--brand); box-shadow: 0 0 0 3px var(--brand-tint); }
  .find svg { width: 1.1rem; height: 1.1rem; fill: none; stroke: var(--ink-faint); stroke-width: 2; stroke-linecap: round; flex: 0 0 auto; }
  .find input { flex: 1 1 auto; border: 0; background: transparent; font: inherit; min-height: calc(var(--tap) - 4px); outline: none; }

  /* ---- site cards ------------------------------------------------------ */
  .sites { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.6rem; }
  .sites li {
    animation: rise 220ms ease-out both;
    animation-delay: calc(var(--i, 0) * 35ms);
  }
  @keyframes rise { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }

  .site {
    position: relative;
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-template-areas: 'rail main chev' 'rail counts chev' 'rail when chev';
    align-items: center;
    gap: 0.35rem 0.8rem;
    padding: 0.9rem 0.9rem 0.9rem 0;
    background: var(--paper);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow);
    color: inherit;
    text-decoration: none;
    overflow: hidden;
    transition: transform 200ms ease-out, box-shadow 200ms ease-out;
  }
  .site:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgb(16 22 29 / 0.12); }
  .site:active { transform: scale(0.995); }
  .site:focus-visible { outline: 3px solid var(--brand); outline-offset: 2px; }

  .site__rail { grid-area: rail; align-self: stretch; width: 6px; background: var(--accent); margin-right: 0.6rem; }
  .site__rail--done { background: var(--line); }
  .site__main { grid-area: main; display: grid; gap: 0.1rem; min-width: 0; }
  .site__name { font-size: 1.15rem; font-weight: 700; letter-spacing: -0.01em; }
  .site__place { font-size: 0.86rem; color: var(--ink-soft); }
  .site__counts { grid-area: counts; display: flex; gap: 0.9rem; flex-wrap: wrap; }
  .site__count { display: grid; text-align: center; line-height: 1.1; }
  .site__count strong { font-size: 1.05rem; font-variant-numeric: tabular-nums; }
  .site__count small { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.06em; color: var(--ink-faint); }
  .site__when { grid-area: when; display: flex; gap: 0.4rem; align-items: baseline; font-size: 0.8rem; color: var(--ink-soft); }
  .site__when strong { font-variant-numeric: tabular-nums; color: var(--ink); }
  .site__chev { grid-area: chev; width: 1.3rem; height: 1.3rem; fill: none; stroke: var(--ink-faint); stroke-width: 2.2; stroke-linecap: round; stroke-linejoin: round; }

  @media (min-width: 48rem) {
    .site {
      grid-template-columns: auto 1.3fr auto auto auto;
      grid-template-areas: 'rail main counts when chev';
      gap: 0 1.2rem;
    }
    .site__counts { gap: 1.2rem; }
    .site__when { flex-direction: column; align-items: flex-end; gap: 0; }
  }

  .sites--finished .site { background: var(--bg-sunken); box-shadow: none; }

  .finished-toggle {
    display: inline-flex; align-items: center; gap: 0.4rem;
    margin: 0.9rem 0 0.5rem; padding: 0.4rem 0.2rem; min-height: var(--tap);
    border: 0; background: transparent; font: inherit; font-weight: 700; color: var(--ink-soft); cursor: pointer;
  }
  .finished-toggle svg { width: 1.1rem; height: 1.1rem; fill: none; stroke: currentColor; stroke-width: 2.2; stroke-linecap: round; transition: transform 200ms ease-out; }
  .finished-toggle svg.open { transform: rotate(180deg); }

  .empty {
    display: grid; gap: 0.6rem; justify-items: start;
    background: var(--paper); border-radius: var(--radius-lg); padding: 1.2rem; box-shadow: var(--shadow);
  }

  @media (prefers-reduced-motion: reduce) {
    .sites li { animation: none; }
    .site, .hero__cta, .finished-toggle svg { transition: none; }
    .site:hover { transform: none; }
  }
</style>
